// A minimal, guest-only player identity for the multiplayer relay.
//
// The relay's connection token (POST /players/me/relay-token, sstk-studio-api)
// requires an already-authenticated player - the same signup/login model
// sstk-studio-api's players use, not a new one. This repo's generic
// /play/<slug> runtime never had a reason to authenticate a player before
// now (playClient.ts's public play surface is deliberately unauthenticated),
// so there is nothing to reuse here: a per-browser guest identity is signed
// up once per published game and cached, purely so the relay's presence list
// has a stable id/name to show. This is intentionally NOT a real account
// (no password recovery, no cross-device identity) - a creator who wants
// real accounts for their players already has the full signup/login flow to
// build a proper screen against; this is the minimal floor the presence demo
// needs.
//
// Deliberately does not use services/api.ts's apiGet/apiPost: that wrapper
// authenticates every request with the signed-in creator's Cognito session
// (getIdToken) and routes any 401 to the creator sign-in screen
// (setAuthExpiredHandler) - both wrong for a player token on the public play
// surface, where the visitor is very often not a signed-in creator at all.

import { API_URL } from '../../services/api';

const STORAGE_PREFIX = 'sstk-relay-player:';

interface StoredPlayerSession {
  token: string;
  playerId: string;
  name: string;
}

interface PlayerSignupResponse {
  token: string;
  player: { id: string; handle: string; display_name: string | null };
}

export interface RelayTokenResponse {
  token: string;
  wsUrl: string;
  room: string;
}

function randomSuffix(): string {
  return Math.random().toString(36).slice(2, 10);
}

function storageKey(gameId: string): string {
  return `${STORAGE_PREFIX}${gameId}`;
}

function loadSession(gameId: string): StoredPlayerSession | null {
  try {
    const raw = localStorage.getItem(storageKey(gameId));
    return raw ? (JSON.parse(raw) as StoredPlayerSession) : null;
  } catch {
    return null;
  }
}

function saveSession(gameId: string, session: StoredPlayerSession): void {
  try {
    localStorage.setItem(storageKey(gameId), JSON.stringify(session));
  } catch {
    // Private browsing / storage disabled: the session just will not persist
    // across reloads, so the next mount signs up a fresh guest instead.
  }
}

function clearSession(gameId: string): void {
  try {
    localStorage.removeItem(storageKey(gameId));
  } catch {
    // See saveSession.
  }
}

async function signupGuest(gameId: string): Promise<StoredPlayerSession> {
  const resp = await fetch(`${API_URL}/games/${encodeURIComponent(gameId)}/players/signup`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ handle: `guest-${randomSuffix()}`, password: randomSuffix() + randomSuffix() }),
  });
  if (!resp.ok) throw new Error(`Guest signup failed: ${resp.status}`);
  const body = (await resp.json()) as PlayerSignupResponse;
  const session: StoredPlayerSession = {
    token: body.token,
    playerId: body.player.id,
    name: body.player.display_name || body.player.handle,
  };
  saveSession(gameId, session);
  return session;
}

async function ensurePlayerSession(gameId: string): Promise<StoredPlayerSession> {
  return loadSession(gameId) ?? (await signupGuest(gameId));
}

/**
 * Synchronously reads this browser's cached relay player id for `gameId`,
 * if a guest session has already been signed up and cached (see
 * `saveSession`) - never triggers a signup network call itself, unlike
 * `ensurePlayerSession`. Returns null before any session exists yet for
 * this game, when storage is unavailable, or when `gameId` is undefined
 * (nothing to have signed up for). This is the real relay-issued id
 * `ClientLogicRuntime`'s `localPlayerId` option needs once a relay
 * connection is open (`useRelayConnection`'s `status`), replacing the
 * single-player runtime's own hardcoded fallback id.
 */
export function getCachedPlayerId(gameId: string | undefined): string | null {
  if (!gameId) return null;
  return loadSession(gameId)?.playerId ?? null;
}

/**
 * Exchanges a (cached, or freshly signed-up guest) player session token for a
 * short-lived relay token. A 401 (expired/invalid cached session - the
 * player token's TTL, or a stale token from a previous deploy) clears the
 * cache and retries once with a brand new guest identity, since that retry
 * is cheap and this should never surface as a user-facing error by itself.
 */
export async function fetchRelayToken(gameId: string): Promise<RelayTokenResponse> {
  const session = await ensurePlayerSession(gameId);
  const resp = await fetch(`${API_URL}/players/me/relay-token`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${session.token}` },
  });
  if (resp.status === 401) {
    clearSession(gameId);
    const fresh = await signupGuest(gameId);
    const retry = await fetch(`${API_URL}/players/me/relay-token`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${fresh.token}` },
    });
    if (!retry.ok) throw new Error(`Relay token request failed: ${retry.status}`);
    return (await retry.json()) as RelayTokenResponse;
  }
  if (!resp.ok) throw new Error(`Relay token request failed: ${resp.status}`);
  return (await resp.json()) as RelayTokenResponse;
}
